<script>
/* global axios */
// Per-channel WhatsApp template management for the 1msg provider: list, full builder, duplicate,
// fix-and-resubmit, delete, auto re-sync while anything is pending.
// Plans: docs/smarticks/whatsapp-templates-crud-plan.md, docs/smarticks/whatsapp-template-builder-plan.md.
// Renders the RAW message_templates (every status) from our admin controller — NOT the approved-only
// getFilteredWhatsAppTemplates getter. Sync reuses the native inboxes/:id/sync_templates endpoint.
//
// Lifecycle rules (plan §3 L1–L5, measured live 2026-10-03):
// - 1msg deletes a template by NAME, in every language.
// - A just-deleted name cannot be recreated at once ("language is being deleted"), and Meta blocks an
//   approved template's name for 30 days. So "fix" always resubmits under a NEW name, and deletes the
//   old rejected one only when no other language shares its name.
import { useAlert } from 'dashboard/composables';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TemplateBuilder from 'dashboard/components/widgets/smarticks/whatsappTemplates/TemplateBuilder.vue';
import { serverErrorMessage } from 'dashboard/helper/whatsappTemplateMessages';

const REASONS = [
  'ABUSIVE_CONTENT',
  'CATEGORY_NOT_AVAILABLE',
  'INCORRECT_CATEGORY',
  'INVALID_FORMAT',
  'PROMOTIONAL',
  'SCAM',
  'TAG_CONTENT_MISMATCH',
];
const KNOWN_STATUSES = [
  'approved',
  'pending',
  'rejected',
  'archived',
  'paused',
  'disabled',
  'pending_deletion',
];
const POLL_MS = 60 * 1000;
const POLL_LIMIT_MS = 30 * 60 * 1000;

export default {
  name: 'WhatsappTemplatesPage',
  components: { BaseTable, BaseTableRow, BaseTableCell, NextButton, TemplateBuilder },
  props: {
    inbox: { type: Object, required: true },
  },
  data() {
    return {
      templates: [],
      loading: true,
      loadFailed: false,
      syncing: false,
      showDelete: false,
      showArchived: false,
      deleteTarget: null,
      builder: null, // { mode, template }
      pollTimer: null,
      pollStartedAt: null,
      pollStopped: false,
    };
  },
  computed: {
    accountId() {
      return this.$route.params.accountId;
    },
    inboxId() {
      return this.inbox.id;
    },
    tableHeaders() {
      return [
        this.$t('WA_TEMPLATES.COL.NAME'),
        this.$t('WA_TEMPLATES.COL.LANGUAGE'),
        this.$t('WA_TEMPLATES.COL.CATEGORY'),
        this.$t('WA_TEMPLATES.COL.STATUS'),
        '',
      ];
    },
    archivedCount() {
      return this.templates.filter(
        tpl => this.statusKind(tpl.status) === 'archived'
      ).length;
    },
    visibleTemplates() {
      if (this.showArchived) return this.templates;
      return this.templates.filter(
        tpl => this.statusKind(tpl.status) !== 'archived'
      );
    },
    hasPending() {
      return this.templates.some(
        tpl => this.statusKind(tpl.status) === 'pending'
      );
    },
    deleteLanguages() {
      if (!this.deleteTarget) return '';
      return this.siblings(this.deleteTarget, true)
        .map(tpl => tpl.language)
        .join(', ');
    },
    deleteBlocksName() {
      return (
        !!this.deleteTarget &&
        this.siblings(this.deleteTarget, true).some(
          tpl => this.statusKind(tpl.status) === 'approved'
        )
      );
    },
  },
  watch: {
    hasPending(value) {
      if (value) this.startPolling();
      else this.stopPolling();
    },
  },
  mounted() {
    this.fetchTemplates();
    document.addEventListener('visibilitychange', this.onVisibility);
  },
  beforeUnmount() {
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.onVisibility);
  },
  methods: {
    async fetchTemplates() {
      this.loading = true;
      this.loadFailed = false;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/smarticks/onemsg/templates`,
          { params: { inbox_id: this.inboxId } }
        );
        this.applyTemplates(data.templates || []);
      } catch (error) {
        this.loadFailed = true;
      } finally {
        this.loading = false;
      }
    },
    // Toast when a template the admin is waiting on changes state.
    applyTemplates(next) {
      const before = Object.fromEntries(
        this.templates.map(tpl => [`${tpl.name}:${tpl.language}`, tpl.status])
      );
      next.forEach(tpl => {
        const previous = before[`${tpl.name}:${tpl.language}`];
        if (this.statusKind(previous) !== 'pending') return;
        const now = this.statusKind(tpl.status);
        if (now === 'approved') {
          useAlert(this.$t('WA_TEMPLATES.POLL.APPROVED', { name: tpl.name }));
        } else if (now === 'rejected') {
          useAlert(this.$t('WA_TEMPLATES.POLL.REJECTED', { name: tpl.name }));
        }
      });
      this.templates = next;
    },
    async syncNow({ quiet = false } = {}) {
      this.syncing = true;
      try {
        await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/sync_templates`
        );
        await this.fetchTemplates();
        if (!quiet) useAlert(this.$t('WA_TEMPLATES.SYNC.DONE'));
      } catch (error) {
        if (!quiet) useAlert(this.$t('WA_TEMPLATES.SYNC.FAILED'));
      } finally {
        this.syncing = false;
      }
    },
    startPolling() {
      if (this.pollTimer) return;
      this.pollStartedAt = Date.now();
      this.pollStopped = false;
      this.pollTimer = setInterval(this.pollTick, POLL_MS);
    },
    stopPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    pollTick() {
      if (Date.now() - this.pollStartedAt > POLL_LIMIT_MS) {
        this.stopPolling();
        this.pollStopped = true;
        return;
      }
      if (document.visibilityState === 'visible' && !this.builder) {
        this.syncNow({ quiet: true });
      }
    },
    onVisibility() {
      if (document.visibilityState === 'visible' && this.hasPending) {
        this.startPolling();
      }
    },
    openBuilder(mode, template = null) {
      this.builder = { mode, template };
    },
    async onBuilt({ templates, name }) {
      const fixed = this.builder?.mode === 'fix' ? this.builder.template : null;
      this.builder = null;
      this.applyTemplates(templates);
      // "Fix": the corrected copy went in under a new name; remove the old rejected one, but only when
      // no other language shares its name (deleting by name removes every language).
      if (fixed && fixed.name !== name && this.siblings(fixed).length === 0) {
        await this.deleteByName(fixed.name, { quiet: true });
      }
      if (this.hasPending) this.startPolling();
    },
    siblings(template, includeSelf = false) {
      return this.templates.filter(
        tpl =>
          tpl.name === template.name &&
          (includeSelf || tpl.language !== template.language)
      );
    },
    canFix(template) {
      return ['rejected', 'pending', 'paused'].includes(
        this.statusKind(template.status)
      );
    },
    confirmDelete(template) {
      this.deleteTarget = template;
      this.showDelete = true;
    },
    async deleteByName(name, { quiet = false } = {}) {
      try {
        const { data } = await axios.delete(
          `/api/v1/accounts/${this.accountId}/smarticks/onemsg/templates`,
          { params: { inbox_id: this.inboxId, name } }
        );
        this.templates = data.templates || [];
        if (!quiet) useAlert(this.$t('WA_TEMPLATES.DELETE.DONE'));
      } catch (error) {
        // Show WHY when the provider said why (e.g. Meta refuses to delete its own sample templates).
        useAlert(serverErrorMessage(this.$t, error));
      }
    },
    async doDelete() {
      if (!this.deleteTarget) return;
      const { name } = this.deleteTarget;
      this.showDelete = false;
      this.deleteTarget = null;
      await this.deleteByName(name);
    },
    bodyText(template) {
      const c = (template.components || []).find(comp => comp.type === 'BODY');
      return c ? c.text : '';
    },
    statusKind(status) {
      const s = String(status || '').toLowerCase();
      if (s.startsWith('pending') && s !== 'pending_deletion') return 'pending';
      return KNOWN_STATUSES.includes(s) ? s : 'unknown';
    },
    statusClass(status) {
      return (
        {
          approved: 'bg-n-teal-3 text-n-teal-11',
          pending: 'bg-n-amber-3 text-n-amber-11',
          rejected: 'bg-n-ruby-3 text-n-ruby-11',
          paused: 'bg-n-amber-3 text-n-amber-11',
          disabled: 'bg-n-ruby-3 text-n-ruby-11',
        }[this.statusKind(status)] || 'bg-n-slate-3 text-n-slate-11'
      );
    },
    statusIcon(status) {
      return (
        {
          approved: 'i-lucide-circle-check',
          pending: 'i-lucide-clock',
          rejected: 'i-lucide-circle-x',
          archived: 'i-lucide-archive',
          paused: 'i-lucide-circle-pause',
          disabled: 'i-lucide-ban',
        }[this.statusKind(status)] || 'i-lucide-circle-help'
      );
    },
    statusLabel(status) {
      const kind = this.statusKind(status);
      if (kind === 'unknown') {
        return this.$t('WA_TEMPLATES.STATUS.UNKNOWN', { status });
      }
      return this.$t(`WA_TEMPLATES.STATUS.${kind.toUpperCase()}`);
    },
    categoryLabel(category) {
      const key = String(category || '').toUpperCase();
      return ['UTILITY', 'MARKETING', 'AUTHENTICATION'].includes(key)
        ? this.$t(`WA_TEMPLATES.CATEGORY.${key}`)
        : category;
    },
    rejectionReason(template) {
      if (this.statusKind(template.status) !== 'rejected') return '';
      const reason = String(template.rejected_reason || '');
      if (REASONS.includes(reason)) {
        return this.$t(`WA_TEMPLATES.REASON.${reason}`);
      }
      if (!reason || reason === 'NONE') {
        return this.$t('WA_TEMPLATES.REASON.NONE');
      }
      return this.$t('WA_TEMPLATES.REASON.UNKNOWN', { reason });
    },
  },
};
</script>

<template>
  <TemplateBuilder
    v-if="builder"
    :account-id="accountId"
    :inbox-id="inboxId"
    :templates="templates"
    :start="builder"
    @done="onBuilt"
    @cancel="builder = null"
  />
  <div v-else class="flex flex-col gap-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="text-sm text-n-slate-11 max-w-xl">
        {{ $t('WA_TEMPLATES.DESCRIPTION') }}
      </p>
      <div class="flex items-center gap-2 shrink-0">
        <NextButton
          faded
          slate
          :is-loading="syncing"
          icon="i-lucide-refresh-ccw"
          :label="$t('WA_TEMPLATES.SYNC.BUTTON')"
          @click="syncNow()"
        />
        <NextButton
          icon="i-lucide-plus"
          :label="$t('WA_TEMPLATES.CREATE.BUTTON')"
          @click="openBuilder('new')"
        />
      </div>
    </div>

    <p v-if="pollTimer" class="text-xs text-n-slate-11">
      {{ $t('WA_TEMPLATES.POLL.ACTIVE') }}
    </p>
    <p v-else-if="pollStopped" class="text-xs text-n-slate-11">
      {{ $t('WA_TEMPLATES.POLL.STOPPED') }}
    </p>

    <div
      v-if="loadFailed"
      class="flex flex-wrap items-center gap-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak p-3 text-sm text-n-ruby-11"
    >
      <span>{{ $t('WA_TEMPLATES.LOAD_FAILED') }}</span>
      <NextButton
        faded
        slate
        sm
        :label="$t('WA_TEMPLATES.RETRY')"
        @click="fetchTemplates"
      />
    </div>

    <label
      v-if="archivedCount"
      class="flex items-center gap-2 text-sm text-n-slate-11 cursor-pointer"
    >
      <input v-model="showArchived" type="checkbox" />
      {{ $t('WA_TEMPLATES.SHOW_ARCHIVED', { n: archivedCount }) }}
    </label>

    <div class="overflow-x-auto">
      <BaseTable
        :headers="tableHeaders"
        :items="visibleTemplates"
        :loading="loading"
        :no-data-message="$t('WA_TEMPLATES.EMPTY')"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="tpl in items"
            :key="`${tpl.name}-${tpl.language}`"
          >
            <BaseTableCell>
              <bdi class="font-medium text-n-slate-12 font-inter">{{
                tpl.name
              }}</bdi>
              <p class="text-xs text-n-slate-11 truncate max-w-xs" dir="auto">
                {{ bodyText(tpl) }}
              </p>
            </BaseTableCell>
            <BaseTableCell>
              <bdi class="font-inter">{{ tpl.language }}</bdi>
            </BaseTableCell>
            <BaseTableCell>{{ categoryLabel(tpl.category) }}</BaseTableCell>
            <BaseTableCell>
              <span
                class="inline-flex items-center gap-1 px-2 py-1 text-xs leading-none rounded-lg"
                :class="statusClass(tpl.status)"
              >
                <i :class="statusIcon(tpl.status)" />
                {{ statusLabel(tpl.status) }}
              </span>
              <p
                v-if="rejectionReason(tpl)"
                class="mt-1 text-xs text-n-ruby-11 line-clamp-2 max-w-xs"
                dir="auto"
              >
                {{ rejectionReason(tpl) }}
              </p>
              <details
                v-if="rejectionReason(tpl) && tpl.rejection_recommendation"
                class="mt-1 text-xs text-n-slate-11 max-w-xs"
              >
                <summary class="cursor-pointer">
                  {{ $t('WA_TEMPLATES.REASON.DETAILS') }}
                </summary>
                <p dir="ltr" class="mt-1 break-words">
                  {{ tpl.rejection_recommendation }}
                </p>
              </details>
            </BaseTableCell>
            <BaseTableCell align="end">
              <div class="flex items-center justify-end gap-1">
                <NextButton
                  v-if="canFix(tpl)"
                  faded
                  slate
                  sm
                  icon="i-lucide-wrench"
                  :label="$t('WA_TEMPLATES.ACTIONS.FIX')"
                  @click="openBuilder('fix', tpl)"
                />
                <NextButton
                  ghost
                  slate
                  sm
                  icon="i-lucide-copy"
                  :aria-label="$t('WA_TEMPLATES.ACTIONS.DUPLICATE')"
                  :title="$t('WA_TEMPLATES.ACTIONS.DUPLICATE')"
                  @click="openBuilder('duplicate', tpl)"
                />
                <NextButton
                  ghost
                  slate
                  sm
                  icon="i-lucide-trash-2"
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  :aria-label="$t('WA_TEMPLATES.ACTIONS.DELETE')"
                  :title="$t('WA_TEMPLATES.ACTIONS.DELETE')"
                  @click="confirmDelete(tpl)"
                />
              </div>
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </div>

    <woot-delete-modal
      v-if="showDelete"
      :show="showDelete"
      :on-close="() => (showDelete = false)"
      :on-confirm="doDelete"
      :title="$t('WA_TEMPLATES.DELETE.TITLE')"
      :message="
        $t('WA_TEMPLATES.DELETE.ALL_LANGUAGES', {
          languages: deleteLanguages,
        }) +
        (deleteBlocksName
          ? ` ${$t('WA_TEMPLATES.DELETE.NAME_BLOCK', { name: deleteTarget.name })}`
          : '')
      "
      :message-value="deleteTarget ? deleteTarget.name : ''"
      :confirm-text="$t('WA_TEMPLATES.DELETE.CONFIRM')"
      :reject-text="$t('WA_TEMPLATES.DELETE.REJECT')"
    />
  </div>
</template>
